import { Component, EventEmitter, Input, OnInit, Output, ViewChild, OnChanges, ElementRef } from "@angular/core";
import { SHORTCUT_LIST_SELECTOR } from "../../../crossconcern/utilities/properties/selector.property";
import { BaseComponent } from "../../base/base.component";
import { AppComponent } from "../../app.component";
import { Router } from "@angular/router";
import { Location } from "@angular/common";
import { AlertUtils } from "../../../crossconcern/utilities/utils/alert.utils";
import { TableInfo } from "../../../crossconcern/models/tablelist/tableinfo.model";
import { TableInfoHeader } from "../../../crossconcern/models/tablelist/tableinfoheader.model";
import { ButtonsModel } from "../../../crossconcern/models/tablelist/button.model";
import {
    TABLELIST_BUTTON_COLOR_4, TABLELIST_BUTTON_ICON_CLASS_EDIT,
    TABLELIST_BUTTON_ICON_CLASS_VIEW
} from "../../../crossconcern/utilities/properties/tablelist.property";
import { NurseryManager } from "../../../business/managers/nursery.manager";
import { ShortcutList } from "../../../datastore/entities/shortcutlist.model";
import { finalize } from "rxjs/operators";
import { FormInfo } from "../../../crossconcern/models/form/forminfo";
import { FormInfoField } from "../../../crossconcern/models/form/forminfofield";
import {
    FORM_TYPE_TEXT, FORM_TYPE_TEXTAREA,
    FORM_WIDTH_COL_12,
    FORM_WIDTH_OFFSET_COL_0
} from "../../../crossconcern/utilities/properties/base.property";
import { Validators } from "@angular/forms";
import { ShortcutType } from "../../../crossconcern/types/shortcut.type";

@Component({
    selector: SHORTCUT_LIST_SELECTOR,
    templateUrl: "./shortcutlist.component.html"
})
export class ShortcutListComponent extends BaseComponent implements OnInit, OnChanges {

    @Input() public nurseryUUID: string;
    @Input() public photosphereUUID: string;
    @Input() public editEnabled: boolean;
    @Output() public sphereChosen: EventEmitter<string> = new EventEmitter<string>();
    @Output() public closeSection: EventEmitter<{}> = new EventEmitter();
    @Output() public shortcutAdded: EventEmitter<{
        title: string,
        desc: string
    }> = new EventEmitter<{ title: string, desc: string }>();

    public formInfo: FormInfo;
    public formComponent;
    @ViewChild("formShortcut", null) set content(content: ElementRef) {
        if(content) { // initially setter gets called with undefined
            this.formComponent = content;
        }
    }

    public tableInfo: TableInfo;

    public isAdding = false;

    private sortField: string;
    private sortAsc: boolean;

    private shortcuts: ShortcutList;
    private editedShortcut: number;

    constructor(
        protected appComponent: AppComponent,
        protected router: Router,
        protected location: Location,
        protected alertUtils: AlertUtils,
        private nurseryManager: NurseryManager) {
        super(appComponent, router, alertUtils, location);
    }

    ngOnInit(): void {
        const formInfoFields: FormInfoField[] = [
            new FormInfoField(
                FORM_TYPE_TEXT,
                "SHORTCUT_LIST_COMPONENT_TITLE",
                "title",
                [
                    Validators.required
                ],
                null,
                null,
                null,
                true,
                true,
                null,
                FORM_WIDTH_COL_12,
                FORM_WIDTH_OFFSET_COL_0),
            new FormInfoField(
                FORM_TYPE_TEXTAREA,
                "SHORTCUT_LIST_COMPONENT_DESC",
                "desc",
                [
                    Validators.required
                ],
                null,
                null,
                null,
                true,
                true,
                null,
                FORM_WIDTH_COL_12,
                FORM_WIDTH_OFFSET_COL_0)
        ];

        this.formInfo = new FormInfo(formInfoFields, null);
    }

    ngOnChanges(): void {
        this.listUpdate();
    }

    private setTable() {
        const headers = {
            "shortcut": new TableInfoHeader(
                "SHORTCUT_LIST_COMPONENT_HEADER_NAME",
                false,
                this.sortAsc === true,
                this.sortField === "shortcut"
            ),
            "actions": new TableInfoHeader(
                "SHORTCUT_LIST_COMPONENT_HEADER_ACTIONS",
                false,
                false,
                false
            )
        };

        const items: unknown[] = [];
        for (const shortcut of this.shortcuts.shortcuts) {
            items.push({ "shortcut": shortcut.title ? shortcut.title : "" });
        }

        const buttons: ButtonsModel[] = [];
        buttons.push(new ButtonsModel(
            "SHORTCUT_LIST_COMPONENT_TABLE_BUTTON_VIEW",
            TABLELIST_BUTTON_COLOR_4,
            TABLELIST_BUTTON_ICON_CLASS_VIEW,
            (item) => {
                this.onViewSphereClick(item as number);
            })
        );
        if(this.editEnabled) {
            buttons.push(new ButtonsModel(
                "SHORTCUT_LIST_COMPONENT_TABLE_BUTTON_EDIT",
                TABLELIST_BUTTON_COLOR_4,
                TABLELIST_BUTTON_ICON_CLASS_EDIT,
                (item) => {
                    this.editedShortcut = item as number;
                    this.onNewClick();
                })
            );
        }

        this.tableInfo = new TableInfo(headers, items, this.shortcuts.shortcuts.length, buttons);
    }

    private onViewSphereClick(pos: number) {
        if(this.shortcuts.shortcuts[pos] && this.shortcuts.shortcuts[pos].photosphere) {
            this.sphereChosen.emit(this.shortcuts.shortcuts[pos].photosphere.uuid);
        }
    }

    public onCloseClick() {
        this.closeSection.emit();
    }

    public onNewClick() {
        if(this.isAdding) {
            if (this.editedShortcut !== undefined) {
                this.appComponent.startLoading();
                this.nurseryManager.deleteShortcut(this.shortcuts.shortcuts[this.editedShortcut].uuid)
                    .pipe(
                        finalize(() => this.appComponent.stopLoading())
                    )
                    .subscribe(() => {
                        this.listModified();
                    });
            } else {
                this.isAdding = !this.isAdding;
            }
        } else if (this.editedShortcut !== undefined) {
            this.formInfo.fields[0].selectedValue = this.shortcuts.shortcuts[this.editedShortcut].title;
            this.formInfo.fields[1].selectedValue = this.shortcuts.shortcuts[this.editedShortcut].description;
            this.isAdding = !this.isAdding;
            setTimeout(() => this.formComponent.updateFormValues(this.formInfo), 1);
        } else {
            this.isAdding = !this.isAdding;
        }
    }

    /* eslint-disable camelcase, @typescript-eslint/camelcase */
    public onSaveClick() {
        this.appComponent.startLoading();
        if(this.formComponent.form.value.title
            && this.formComponent.form.value.desc
            && (this.photosphereUUID || this.editedShortcut !== undefined)) {
            const shortcut: ShortcutType = {
                title: this.formComponent.form.value.title,
                description: this.formComponent.form.value.desc,
                photosphere_uuid: this.editedShortcut !== undefined ? undefined : this.photosphereUUID
            };
            if(this.editedShortcut !== undefined) {
                this.nurseryManager.editShortcut(this.shortcuts.shortcuts[this.editedShortcut].uuid, shortcut)
                    .pipe(
                        finalize(() => this.appComponent.stopLoading())
                    )
                    .subscribe(() => {
                        this.listModified();
                    });

            } else {
                this.nurseryManager.addShortcut(shortcut)
                    .pipe(
                        finalize(() => this.appComponent.stopLoading())
                    )
                    .subscribe(() => {
                        this.listModified();
                    });
            }
        }
    }

    public listModified() {
        this.editedShortcut = undefined;
        this.formInfo.fields[0].selectedValue = undefined;
        this.formInfo.fields[1].selectedValue = undefined;
        this.isAdding = !this.isAdding;
        setTimeout(() => this.listUpdate(), 1);
    }

    public listUpdate() {
        if(!this.isAdding) {
            this.appComponent.startLoading();
            this.nurseryManager.getShortcuts(10000, 0, this.nurseryUUID)
                .pipe(
                    finalize(() => this.appComponent.stopLoading())
                )
                .subscribe((list) => {
                    this.shortcuts = list;
                    this.setTable();
                });
        }
    }
}